import { useConnectProject } from '@trimble/connect-project-providers';
import { LoadState } from '@trimble/data-loading';
import { createResponseErrorLogger } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { createMemo, JSX } from 'solid-js';

import { useDataContext } from '../data-state/DataContextProvider';
import { FxWorkspaceContextProvider } from './FxWorkspaceContextProvider';
import { createFxWorkspaceRepository } from './fx-workspace-repository.factory';
import type { MissionPathRef } from './map-mission-path-reference';

export function FxWorkspaceRepositoryProvider(props: {
  children: JSX.Element;
  fxWorkspaceDomain?: string;
  clientName: string;
  clientVersion: string;
}): JSX.Element {
  const auth = useAuth();
  const { selectedTenantTrn } = useConnectProject();
  const logger = useLogger().getLogger('FxWorkspace');
  const apiErrorLogger = createResponseErrorLogger(logger);

  const repository = createMemo(() =>
    createFxWorkspaceRepository({
      fxWorkspaceDomain: props.fxWorkspaceDomain,
      clientName: props.clientName,
      clientVersion: props.clientVersion,
      getAccessToken: () => auth().getAccessTokenSilently(),
      getTenantTrn: () => selectedTenantTrn(),
      onResponseError: apiErrorLogger,
    })
  );

  const { dataState } = useDataContext();
  const getMissionPathRefs = (): readonly MissionPathRef[] => {
    const mission = dataState.mission;
    if (mission.state !== LoadState.LoadComplete) {
      return [];
    }
    return Object.values(mission.items).map((item) => ({
      missionTrn: item.missionTrn,
      pathId: item.pathId,
    }));
  };

  const repositoryKey = createMemo(() => {
    const domain = props.fxWorkspaceDomain?.trim();
    if (domain === undefined || domain === '') {
      return '';
    }

    return `${selectedTenantTrn() ?? ''}|${domain}|${props.clientName}|${props.clientVersion}`;
  });

  return (
    <FxWorkspaceContextProvider
      repository={repository}
      repositoryKey={repositoryKey}
      getMissionPathRefs={getMissionPathRefs}
    >
      {props.children}
    </FxWorkspaceContextProvider>
  );
}
